import { JSX, useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom";

import { useTenant } from "features/tenant/context"
import { useUserSession } from "features/session/context"

import {
  SearchListIcon,
  ConfigureIcon,
  LogoutIcon,
  ManualIcon
} from 'shared/icons'

import {
	Notifications as NotificationIcon,
	PersonSearch as MatchingIcon,
	TravelExplore as DpcResultIcon
} from '@mui/icons-material';
import { useGetManualURL } from "interface/tenant";

type SideMenuItem = {
  label: string
  icon?: JSX.Element
  badge?: number
  selected?: boolean
  disabled?: boolean
  callback: () => void
}

type SideMenuList = {
  key: string
  label: string
  commands: string[]
}

type useSideMenuProps = {
	unreadNotificationCount: number
  onLogoff: (keepUrl: boolean) => void
  onOpenNotification: () => void
}
export const useSideMenu = ({
  unreadNotificationCount,
  onLogoff,
  onOpenNotification
}: useSideMenuProps) => {
  const navigate = useNavigate()
  const tenant = useTenant()
	const userSession = useUserSession()

  const [activePage, setActivePage] = useState<string>('')
  const [sideBarOpen, setSideBarOpen] = useState<boolean>(false)

  const {
    //loading: manualLoading,
    //error: manualError,
    data: manualURL
  } = useGetManualURL(tenant.tenantId)

  const openManual = useCallback(
    () => {
      if (manualURL) window.open(manualURL, '_blank')
    },
    [manualURL]
  )

	const menuItems = useMemo<SideMenuList[]>(
    () => [
      { key: 'search', label: '分析', commands: ['searchList'] },
      { key: 'searchTool', label: '検索ツール', commands: ['matching', 'dpcResult'] },
      { key: 'other', label: 'その他', commands: [ 'configure', 'notification', 'manual', 'logout' ]},
    ],
    []
  )
  const menuCommands = useMemo<{ [index: string]: SideMenuItem }>(
    () => ({
      searchList: {
        label: '分析一覧',
        icon: <SearchListIcon />,
        selected: activePage === 'searchList',
        callback: () => navigate('../search/list'),
      },
      matching: {
        label: '名寄せ',
        icon: <MatchingIcon />,
        selected: activePage === 'matching',
        callback: () => window.open(`/${tenant.domain}/matching`, '_blank', 'noopener, noreferrer'),
      },
      dpcResult: {
        label: '施設実績検索',
        selected: activePage === 'dpcResult',
        icon: <DpcResultIcon />,
        callback: () => window.open(`/${tenant.domain}/dpcResult`, '_blank', 'noopener, noreferrer'),
      },
      configure: {
        label: '管理画面',
        icon: <ConfigureIcon />,
        selected: activePage === 'configure',
        disabled: userSession?.isAdmin !== true,
        callback: () => navigate('../configure')
      },
      notification: {
        label: 'お知らせ',
        icon: <NotificationIcon />,
        badge: unreadNotificationCount,
        callback: () => onOpenNotification(),
      },
      manual: {
        label: '操作マニュアル',
        icon: <ManualIcon />,
        disabled: manualURL ? false : true,
        callback: () => openManual(),
      },
      logout: {
        label: 'ログアウト',
        icon: <LogoutIcon />,
        disabled: false,
        callback: () => onLogoff(false),
      },
    }),
    [
      tenant, activePage, userSession, unreadNotificationCount, navigate,
      manualURL, openManual, onLogoff, onOpenNotification
    ]
  )
	return useMemo(() => ({
    setActivePage,
		sideBarOpen,
		setSideBarOpen,
    menuItems,
    menuCommands
	}), [
    setActivePage,
		sideBarOpen,
		setSideBarOpen,
    menuItems,
    menuCommands
	])
}